import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import Seo from "@/components/Seo";
import PublicLayout from "@/components/PublicLayout";
import VehicleCard from "@/components/VehicleCard";
import { Breadcrumbs, InsuranceSection, HowItWorks, CtaBanner } from "@/components/Marketing";
import FaqList from "@/components/FaqList";
import WhatsAppButton from "@/components/WhatsAppButton";
import { LANDING_PAGES } from "@/data/landing";
import { waMessages } from "@/lib/whatsapp";
import NotFound from "@/pages/NotFound";
import { ArrowRight } from "lucide-react";

export default function Landing() {
  const slug = useLocation().pathname.replace(/^\//, "");
  const page = LANDING_PAGES[slug];
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    if (!page) return;
    api.get("/vehicles", { params: { ...page.filter } }).then((r) => setVehicles(r.data)).catch(() => {});
  }, [slug]);

  if (!page) return <NotFound />;

  const waMsg = page.whatsapp ? waMessages[page.whatsapp]() : waMessages.general();
  const insurancePage = slug.includes("insurance");

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://eazycartrack.co.uk/" },
      { "@type": "ListItem", position: 2, name: page.h1, item: `https://eazycartrack.co.uk/${slug}` },
    ],
  };

  return (
    <PublicLayout>
      <Seo title={page.h1} description={page.seoDescription} path={`/${slug}`} ogImage={page.heroImage} jsonLd={breadcrumbLd} />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: page.h1 }]} />

      <section className="relative overflow-hidden bg-ink text-white">
        <img src={page.heroImage} alt={`${page.h1} — EazyCarTrack`} className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-transparent" />
        <div className="container-x relative py-16 sm:py-20">
          <h1 className="max-w-3xl font-heading text-4xl font-black uppercase leading-[0.95] tracking-tighter sm:text-6xl">{page.h1}</h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-zinc-300">{page.intro}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to="/cars" className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-white px-8 font-bold text-ink transition-transform hover:-translate-y-0.5">
              View Available Cars <ArrowRight className="h-5 w-5" />
            </Link>
            <WhatsAppButton size="lg" message={waMsg} context={`landing:${slug}`} testid="landing-whatsapp">Check Availability on WhatsApp</WhatsAppButton>
          </div>
        </div>
      </section>

      <section className="container-x py-16">
        <div className="grid gap-10 md:grid-cols-2">
          {page.sections.map((s, i) => (
            <div key={i}>
              <h2 className="font-heading text-2xl font-bold tracking-tight">{s.h}</h2>
              <p className="mt-3 leading-relaxed text-zinc-600">{s.p}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-x pb-16">
        <h2 className="font-heading text-3xl font-bold tracking-tight">{page.listHeading}</h2>
        {vehicles.length === 0 ? (
          <div className="mt-8 border border-dashed border-border p-12 text-center text-zinc-500">
            Nothing matching right now — WhatsApp us and we'll tell you what's coming in.
          </div>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {vehicles.map((v, i) => <VehicleCard key={v.id} v={v} index={i} />)}
          </div>
        )}
      </section>

      {insurancePage ? <InsuranceSection /> : <HowItWorks />}

      <FaqList faqs={page.faqs} heading="Common Questions" testidPrefix={`landing-faq`} />

      {page.internalLinks?.length > 0 && (
        <section className="container-x pb-8">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Explore More</h2>
          <ul className="mt-4 flex flex-wrap gap-3">
            {page.internalLinks.map((l, i) => (
              <li key={i}>
                <Link to={l.to} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-surface">
                  {l.label} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <CtaBanner message={waMsg} />
    </PublicLayout>
  );
}
